<template>
    <form-generator
        :schema="formSchema"
        :value="value"
        label-width="40%"
        value-width="60%"
        label-class="text-body-2"
    ></form-generator>
</template>

<script>
import HocFormGenerator from '@/components/FormGenerator/HocFormGenerator';
import FormLayout from '@/components/FormLayout/VerticalFormLayout';
import { mapActions } from 'vuex';

const FormGenerator = HocFormGenerator(FormLayout);

export default {
    name: 'DeviceReadForm',
    components: {
        'form-generator': FormGenerator
    },
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        deviceHealthy: {
            type: Boolean,
            default: false
        },
        deviceDetailsLoading: {
            type: Boolean,
            default: false
        },
        channelOptions: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isTalkingGuideLoading: false,
            isClosedCaptionLoading: false,
            isHealthStatusLoading: false
        };
    },
    computed: {
        allowRefreshHealthStatus() {
            return this.$can('control', 'Device');
        },
        allowChangeTalkingGuide() {
            return (
                this.$can('control', 'Device') &&
                this.$can('show', 'Buttons/devices/changeTalkingGuide') &&
                this.deviceHealthy &&
                !this.isTalkingGuideLoading
            );
        },
        allowChangeClosedCaption() {
            return (
                this.$can('control', 'Device') &&
                this.$can('show', 'Buttons/devices/changeClosedCaption') &&
                this.deviceHealthy &&
                !this.isClosedCaptionLoading
            );
        },
        formSchema() {
            return [
                {
                    name: 'name',
                    label: 'Name',
                    inline: true
                },
                {
                    name: 'healthStatus',
                    label: 'Health status',
                    inline: true,
                    component: {
                        type: 'device-health-icon',
                        props: {
                            loading: this.isHealthStatusLoading,
                            expanded: true
                        },
                        on: {
                            click: this.onHealthStatusClick
                        }
                    }
                },
                {
                    name: 'type',
                    label: 'Device type',
                    inline: true
                },
                {
                    name: 'make',
                    label: 'Make',
                    inline: true
                },
                {
                    name: 'model',
                    label: 'Model',
                    inline: true
                },
                {
                    name: 'hostMac',
                    label: 'Host Mac',
                    inline: true
                },
                {
                    name: 'serialNumber',
                    label: 'Serial Number',
                    inline: true
                },
                {
                    name: 'talkingGuide',
                    label: 'Talking Guide',
                    inline: true,
                    component: {
                        type: 'v-switch',
                        props: {
                            dense: true,
                            hideDetails: true,
                            readonly: !this.allowChangeTalkingGuide,
                            loading:
                                this.isTalkingGuideLoading ||
                                this.deviceDetailsLoading,
                            class: 'my-0 py-0',
                            label: this.value.talkingGuide ? 'On' : 'Off'
                        },
                        on: {
                            change: this.onTalkingGuideClick
                        }
                    }
                },
                {
                    name: 'closedCaption',
                    label: 'Closed Caption',
                    inline: true,
                    component: {
                        type: 'v-switch',
                        props: {
                            dense: true,
                            hideDetails: true,
                            readonly: !this.allowChangeClosedCaption,
                            loading:
                                this.isClosedCaptionLoading ||
                                this.deviceDetailsLoading,
                            class: 'my-0 py-0',
                            label: this.value.closedCaption ? 'On' : 'Off'
                        },
                        on: {
                            change: this.onClosedCaptionClick
                        }
                    }
                },
                {
                    name: 'deviceRoom',
                    label: 'Room',
                    inline: true,
                    props: {
                        loading: this.deviceDetailsLoading
                    }
                },
                {
                    name: 'serviceAccountName',
                    label: 'Service Account',
                    inline: true
                },
                {
                    name: 'defaultChannel',
                    label: 'Default Channel',
                    inline: true
                },
                {
                    name: 'tags',
                    inline: true,
                    hideLabel: true,
                    valueClass: '',
                    component: {
                        type: 'tags-chip-group',
                        props: {
                            label: 'Tags',
                            maxDisplayCount: 2,
                            outlined: true,
                            noDataText: '(No tags selected)',
                            itemProps: {
                                small: true
                            }
                        }
                    }
                }
            ];
        }
    },
    methods: {
        ...mapActions('device', [
            'fetchHealthStatus',
            'updateTalkingGuide',
            'updateClosedCaption'
        ]),
        onHealthStatusClick() {
            this.refreshHealthStatus();
        },
        onTalkingGuideClick(value) {
            this.changeTalkingGuide(value);
        },
        onClosedCaptionClick(value) {
            this.changeClosedCaption(value);
        },
        async refreshHealthStatus() {
            try {
                this.isHealthStatusLoading = true;
                if (this.value && this.value.hostMac) {
                    await this.fetchHealthStatus({
                        hostMac: this.value.hostMac
                    });
                }
            } finally {
                this.isHealthStatusLoading = false;
            }
        },
        async changeTalkingGuide(value) {
            try {
                this.isTalkingGuideLoading = true;
                await this.updateTalkingGuide({
                    deviceId: this.value.id,
                    talkingGuide: value
                });
            } finally {
                this.isTalkingGuideLoading = false;
            }
        },
        async changeClosedCaption(value) {
            try {
                this.isClosedCaptionLoading = true;
                await this.updateClosedCaption({
                    deviceId: this.value.id,
                    closedCaption: value
                });
            } finally {
                this.isClosedCaptionLoading = false;
            }
        }
    }
};
</script>

<style scoped lang="scss"></style>
